import { useEffect, useRef, useState } from "react";
import PromptManagementStyles from "./PromptManagement.module.scss";
import UserActionDotsSvg from "../../../../assets/images/UserTable/UserActionDots.svg";
import crossSvg from "../../../../assets/close-line.svg";
import enterArrow from "../../../../assets/enterArrow.svg";

import { useAppDispatch } from "../../../../hooks/store";
import { IconButton, TextField } from "@mui/material";
import Typography from "../../../../components/Typography/Typography";
import { SimpleButton } from "../../../../components/Buttons/Buttons";
import { Popover } from "antd";
import {
  defaultClauseStatus,
  defaultVisibleSynonymsNumber,
  errorsForTTCClauseManagement,
} from "../../constants";
import { deleteKeyword, saveNewSynonym } from "../../store/admin.thunk";
import { useOutsideClick } from "../../../../hooks/common";
import editIcon from "../../../../assets/images/edit.svg";
import deleteIcon from "../../../../assets/images/delete.svg";

const PromptManagementForTTC = ({
  rule,
  idx,
  allWords,
  onKeywordDelete,
}: any) => {
  const dispatch = useAppDispatch();
  const refForSynonyms = useRef<any>();
  const refForPopover = useRef<any>();
  const isNewKeyword = rule?.type === defaultClauseStatus.NEW_KEYWORD;
  const [isEditing, setIsEditing] = useState(false);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [errors, setErrors] = useState({
    Word: "",
    Synonyms: "",
  });

  const [dataObj, setDataObj] = useState({
    Word: rule?.Word,
    Synonyms: rule?.Synonyms?.filter((synonym: string) => synonym),
    type: rule?.type,
    currentSynonym: "",
  });
  const enableCancelBtn = isNewKeyword ? false : dataObj?.Word?.length > 0;
  const enableSaveBtn =
    errors?.Word?.length === 0 &&
    dataObj?.Word?.trim()?.length > 0 &&
    // errors?.Synonyms?.length === 0;
    dataObj?.Synonyms?.length > 0;

  useOutsideClick(refForSynonyms.current, () => setIsPopoverOpen(false));

  const handleChangeOnEdit = (data: {
    [key: string]: string | Array<string>;
  }) => {
    setDataObj({
      ...dataObj,
      ...data,
    });
  };

  const handleWordChange = (e: any) => {
    const val = e.target?.value;
    const wordExists = allWords?.includes(val);

    if (val && wordExists) {
      setErrors({
        ...errors,
        Word: errorsForTTCClauseManagement.WORD.duplicate,
      });
    } else if (!val || val?.length === 0) {
      setErrors({
        ...errors,
        Word: errorsForTTCClauseManagement.WORD.required,
      });
    } else {
      setErrors({
        ...errors,
        Word: "",
      });
    }
    handleChangeOnEdit({ Word: val });
  };

  const handleSynonymChange = (e: any) => {
    const val = e.target?.value;
    refForSynonyms.current = e.target;

    const synonymExists = dataObj?.Synonyms?.includes(val);

    if (!val || val?.length === 0) {
      setErrors({
        ...errors,
        Synonyms: errorsForTTCClauseManagement.SYNONYM.minCharacters,
      });
    } else if (synonymExists) {
      setErrors({
        ...errors,
        Synonyms: errorsForTTCClauseManagement.SYNONYM.duplicate,
      });
    } else {
      setErrors({
        ...errors,
        Synonyms: "",
      });
    }
    handleChangeOnEdit({ currentSynonym: val });
  };

  const handleSynonymRemoval = (synonymText: string) => {
    const synonyms = dataObj?.Synonyms?.slice();
    const index = synonyms?.indexOf(synonymText);
    if (index > -1) {
      if (synonyms?.length === 1) {
        const field = refForSynonyms?.current;
        if (field) {
          field?.focus();
        }
        setErrors({
          ...errors,
          Synonyms: errorsForTTCClauseManagement.SYNONYM.required,
        });
      }

      synonyms.splice(index, 1);
      handleChangeOnEdit({
        Synonyms: synonyms?.filter((item: string) => item),
        currentSynonym: "",
      });
    }
  };

  const handleKeywordDelete = (e: any) => {
    e.stopPropagation();
    setIsPopoverOpen(false);
    dispatch(deleteKeyword({ id: idx }));
    onKeywordDelete && onKeywordDelete();
  };

  const addSynonym = () => {
    if (errors?.Synonyms?.length > 0 || !dataObj?.currentSynonym?.trim())
      return;
    const synonyms = dataObj?.Synonyms?.slice();
    synonyms.push(dataObj?.currentSynonym);
    handleChangeOnEdit({
      Synonyms: synonyms?.filter((item: string) => item),
      currentSynonym: "",
    });
  };

  const handleKeyDownOnSynonym = ({ key, value }: any) => {
    if (key === "Enter") {
      addSynonym();
    }
  };

  const handleOnClose = (e: any) => {
    e.stopPropagation();
    setIsEditing(false);
    setIsPopoverOpen(false);
    setShowAllFiles(false);
    setErrors({
      Word: "",
      Synonyms: "",
    });
    setDataObj({
      Word: rule?.Word,
      Synonyms: rule?.Synonyms,
      type: rule?.type,
      currentSynonym: "",
    });
  };

  const handleSave = async () => {
    setIsEditing(false);
    dispatch(
      saveNewSynonym({
        idx: idx,
        data: {
          Word: dataObj?.Word,
          Synonyms: dataObj?.Synonyms,
          type: defaultClauseStatus.DEFAULT,
        },
      })
    );
  };

  useEffect(() => {
    if (rule?.type === defaultClauseStatus.NEW_KEYWORD) {
      setIsEditing(true);
    }
  }, [rule?.type]);

  const sxProp = isEditing
    ? {
        // border: "1px solid var(--text-field_input_textColor)",
        // padding: "12.5px",
      }
    : {
        border: "none",
        "& fieldset": { border: "none" },
      };

  const textFieldProps = {
    sx: {
      borderRadius: "4px",
      "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: "var(--text-field_prompt_textColor);",
        paddingLeft: "0",
      },
      ...sxProp,
    },
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        padding: "18px 24px",
        gap: "10px",
      }}
    >
      {/* <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        > */}
          {/* {isNewKeyword && (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <Typography
                  fontSize={18}
                  lineHeight="26px"
                  fontWeight={500}
                  color="var(--secondaryBlackColor)"
                >
                  Add Keyword
                </Typography>
              </div>
            </div>
          )} */}
          {/* <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          > */}
          {/* <Typography
              color="#000000CC"
              fontSize={14}
              fontWeight={500}
            >
              Synonyms
            </Typography> */}

          {/* <Popover
              open={isPopoverOpen}
              ref={refForPopover}
              content={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPopoverOpen(false);
                      setIsEditing(true);
                    }}
                    className={PromptManagementStyles.editKeywordBtn}
                  >
                    <Typography
                      fontSize={14}
                      fontWeight={400}
                      lineHeight="17.5px"
                      color="var(--text-field_prompt_textColor)"
                      style={{
                        padding: "8px",
                        width: "188px",
                      }}
                    >
                      Edit Keyword
                    </Typography>
                  </div>
                  <div
                    onClick={handleKeywordDelete}
                    className={PromptManagementStyles.editKeywordBtn}
                  >
                    <Typography
                      fontSize={14}
                      fontWeight={400}
                      lineHeight="17.5px"
                      color="var(--text-field_prompt_textColor)"
                      style={{
                        padding: "8px",
                        width: "188px",
                      }}
                    >
                      Delete
                    </Typography>
                  </div>
                </div>
              }
              trigger="click"
              placement="bottomRight"
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPopoverOpen(!isPopoverOpen);
                }}
              >
                <img src={UserActionDotsSvg} alt="threedots" />
              </IconButton>
            </Popover>
          </div> */}

          {/* <TextField
            type="text"
            style={{
              width: "50%",
            }}
            disabled={!isEditing}
            value={dataObj?.Word}
            onChange={handleWordChange}
            error={errors?.Word?.length > 0}
            helperText={errors?.Word}
            onClick={(e) => {
              e.stopPropagation();
            }}
            ref={refForSynonyms}
            slotProps={{
              input: {
                style: {
                  fontSize: "18px",
                  lineHeight: "26px",
                  fontWeight: 600,
                  color: "var(--secondaryBlackColor)",
                },
              },
            }}
            {...textFieldProps}
          /> */}
        {/* </div>
      </div> */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {isNewKeyword ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <Typography
                  fontSize={14}
                  lineHeight="26px"
                  fontWeight={500}
                  color="#000000CC"
                >
                  Add New Keyword
                </Typography>
              </div>
            </div>
          ) : (
          <Typography color="#000000CC" fontSize={14} fontWeight={500}>
            Keyword and Synonyms
          </Typography>
          )}
          <div style={{display: "flex", gap: "12px"}}>
            <IconButton
              onClick={(e) => {
                setIsEditing(true);
              }}
            >
              <img src={editIcon} alt="" />
            </IconButton>
            <IconButton onClick={handleKeywordDelete}>
              <img src={deleteIcon} alt="" />
            </IconButton>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: "45%",
            }}
          >
            {isEditing && <Typography fontSize={12} fontWeight={400} color="#000000E5">Keyword</Typography>}
            <TextField
              type="text"
              style={{
                // width: "50%",
                // padding: "12px 16px",
              }}
              disabled={!isEditing}
              value={dataObj?.Word}
              onChange={handleWordChange}
              error={errors?.Word?.length > 0}
              helperText={errors?.Word}
              onClick={(e) => {
                e.stopPropagation();
              }}
              ref={refForSynonyms}
              slotProps={{
                input: {
                  style: {
                    fontSize: "12px",
                    lineHeight: "26px",
                    fontWeight: 500,
                    color: "var(--lightBlackColor)",
                    background: isEditing ? "var(--primaryWhiteColor)" : "",
                  },
                },
              }}
              {...textFieldProps}
            />
          </div>

          {isEditing && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "45%",
              }}
            >
              <Typography fontSize={12} fontWeight={400} color="#000000E5">Synonyms</Typography>
              <TextField
                type="text"
                style={{
                  // width: "50%",
                }}
                disabled={!isEditing}
                value={dataObj?.currentSynonym}
                onChange={handleSynonymChange}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                error={errors?.Synonyms?.length > 0}
                helperText={errors?.Synonyms}
                onKeyDown={handleKeyDownOnSynonym}
                slotProps={{
                  input: {
                    style: {
                      fontSize: "12px",
                      lineHeight: "15px",
                      fontWeight: 400,
                      color: "var(--secondaryBlackColor)",
                      background: "var(--primaryWhiteColor)",
                    },
                    // endAdornment: (
                    //   <div
                    //     className={PromptManagementStyles.enterButton}
                    //     onClick={addSynonym}
                    //   >
                    //     <img src={enterArrow} alt="" />
                    //   </div>
                    // ),
                  },
                }}
                {...textFieldProps}
              />
              <Typography fontSize={12} color="var(--label-grey-textColor)">
                Input value and press enter to add to the list
              </Typography>
            </div>
          )}
        </div>

        <div className={PromptManagementStyles.synonymPillContainer}>
          {!showAllFiles &&
          dataObj?.Synonyms?.length > defaultVisibleSynonymsNumber ? (
            <>
              {dataObj?.Synonyms?.slice(0, defaultVisibleSynonymsNumber)?.map(
                (synonym: any) => (
                  <SynonymPill
                    isEditing={isEditing}
                    synonym={synonym}
                    handleSynonymRemoval={handleSynonymRemoval}
                  />
                )
              )}

              <ShowMoreLessBtn
                isEditing={isEditing}
                showAllFiles={showAllFiles}
                setShowAllFiles={setShowAllFiles}
                text={`+ ${
                  dataObj?.Synonyms?.length - defaultVisibleSynonymsNumber
                } more`}
              />
            </>
          ) : (
            <>
              {dataObj?.Synonyms?.map((synonym: any) => (
                <SynonymPill
                  isEditing={isEditing}
                  synonym={synonym}
                  handleSynonymRemoval={handleSynonymRemoval}
                />
              ))}
              {dataObj?.Synonyms?.length > defaultVisibleSynonymsNumber && (
                <ShowMoreLessBtn
                  isEditing={isEditing}
                  showAllFiles={showAllFiles}
                  setShowAllFiles={setShowAllFiles}
                  text="Show less"
                />
              )}
            </>
          )}
        </div>
      </div>

      <div>
        {isEditing && (
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              justifyContent: "flex-end"
              // marginLeft: "auto",
              // marginRight: "15px",
            }}
          >
            <SimpleButton
              variant="outlined"
              onClick={handleOnClose}
              disabled={!enableCancelBtn}
              style={{ borderRadius: "24px" }}
            >
              <Typography fontSize={12} fontWeight={500} lineHeight="20px">
                Cancel
              </Typography>
            </SimpleButton>
            <SimpleButton
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              disabled={!enableSaveBtn}
              style={{ borderRadius: "24px" }}
            >
              <Typography fontSize={12} fontWeight={500} lineHeight="20px">
                Save
              </Typography>
            </SimpleButton>
          </div>
        )}
      </div>
    </div>
  );
};

const SynonymPill = ({ isEditing, synonym, handleSynonymRemoval }: any) => {
  return (
    <div
      className={PromptManagementStyles.synonymPill}
      style={{
        backgroundColor: "#EBF3FF",
      }}
    >
      <Typography
        fontSize={10}
        fontWeight={400}
        lineHeight={isEditing ? "16.5px" : "20px"}
        color="#000000CC"
        key={synonym}
      >
        {synonym}
      </Typography>
      {isEditing && (
        <div
          className={PromptManagementStyles.crossIcon}
          onClick={() => handleSynonymRemoval(synonym)}
        >
          <img src={crossSvg} alt="" />
        </div>
      )}
    </div>
  );
};

const ShowMoreLessBtn = ({
  isEditing,
  showAllFiles,
  setShowAllFiles,
  text,
}: any) => {
  return (
    <div
      className={PromptManagementStyles.showMoreLessBtn}
      onClick={() => setShowAllFiles(!showAllFiles)}
    >
      <Typography
        color="var(--show-more-less-pill-blueColor)"
        fontSize={isEditing ? 14 : 16}
        fontWeight={400}
        lineHeight={isEditing ? "16.5px" : "20px"}
      >
        {text}
      </Typography>
    </div>
  );
};

export default PromptManagementForTTC;
